import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { confirmFieldRedzone, getFieldShapesAdmin, getFieldSizeUpdates, getFieldVerificationShape, verifyField } from "services";

import {
  formatDayDate,
  getAdminVerificationYearlyDataYear,
  getCenter,
  getEditingRequestButtonState,
  useGetFieldPractices,
  useTranslation,
  useUpdateFieldShapeAdmin,
} from "utils";

import {
  CONFIRM_FIELD_REDZONE_ADMIN,
  DEFAULT_CACHE,
  GET_FIELDS_SHAPE_ADMIN,
  GET_FIELD_SIZE_UPDATES,
  GET_FIELD_VERIFICATIONS,
  GET_FIELD_VERIFICATION_SHAPE,
  VERIFY_FIELD,
  getUnitDisplayLabel,
} from "consts";

import { CROP_TYPE_FCPO, Coordinates, EDITING_REQUEST_TYPE, EditingRequestType, FieldShape, FieldVerification, VerificationEditingRequest } from "models";

import {
  AdminDescriptions,
  AdminTable,
  Button,
  Card,
  Col,
  Drawer,
  ErrorBoundary,
  Icon,
  Link,
  MapInteractive,
  Modal,
  Row,
  SkeletonWrapper,
  Tag,
  Typography,
  Message as message,
} from "components";

import { FieldShapeVerification } from "pages/admin/farms/components/FieldShapeVerification";
import { FieldAdmin } from "pages/admin/verifications//QuickFieldAccess";
import { FieldSizeVarianceCalculatorForm } from "pages/admin/verifications/components/forms/FieldSizeVarianceCalculatorForm";
import { FieldVerificationForm } from "pages/admin/verifications/components/forms/FieldVerificationForm";
import { FieldVerificationSizeForm } from "pages/admin/verifications/components/forms/FieldVerificationSizeForm";
import { EditingRequestModal } from "pages/admin/verifications/components/misc/EditingRequestModal";
import { VerificationComments } from "pages/admin/verifications/components/misc/VerificationComments";
import { FieldLocation } from "pages/app/fields/components/FieldLocation";

type VerificationStatus = true | false | null;

type FieldDrawerProps = {
  field: Omit<FieldVerification, "totalFieldModuleVerifications" | "commentCount" | "practiceParticipations" | "regionId" | "hasSoilPlus3Data">;
  farm: FieldAdmin["farmAddress"];
  farmId: string;
  visible: boolean;
  loadingFields: boolean;
  onClose: () => void;
  onDataChange: () => void;
  payoutApproved: boolean;
  selectedFieldVersion?: number;
  editingRequests?: VerificationEditingRequest[];
};

export const FieldDrawer = ({
  field,
  farm,
  farmId,
  visible,
  loadingFields,
  onClose,
  onDataChange,
  payoutApproved,
  selectedFieldVersion,
  editingRequests = [],
}: FieldDrawerProps) => {
  const [commentCount, setCommentCount] = useState(0);
  const [fieldShape, setFieldShape] = useState<FieldShape | null>(null);
  const [location, setLocation] = useState<Coordinates | null>(null);
  const [fieldSize, setFieldSize] = useState<number | null>(field.size);
  const [showFieldShapeDrawer, setShowFieldShapeDrawer] = useState(false);
  const [showFieldEditDrawer, setShowFieldEditDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRedzoneModal, setShowRedzoneModal] = useState(false);
  const [editingRequestField, setEditingRequestField] = useState<FieldDrawerProps["field"] | null>(null);

  const queryClient = useQueryClient();

  // Filter editing requests to only include field-level requests (fieldShape/fieldSplit/fieldRedzone) for this specific field
  const FIELD_LEVEL_TYPES: EditingRequestType[] = [EDITING_REQUEST_TYPE.FIELD_SHAPE, EDITING_REQUEST_TYPE.FIELD_SPLIT, EDITING_REQUEST_TYPE.FIELD_REDZONE];
  const fieldEditingRequests = editingRequests.filter((request) => request.fieldId === field.id && FIELD_LEVEL_TYPES.includes(request.requestType));
  const buttonState = getEditingRequestButtonState(fieldEditingRequests);
  const verificationYear = getAdminVerificationYearlyDataYear();

  // We need to get the shapes for the whole farm, because the association between field and shape is one to one,
  // so this is kind of a workaround
  const { data: fieldShapeResponse, isLoading: isLoadingFieldShapes } = useQuery({
    enabled: visible,
    queryKey: [GET_FIELDS_SHAPE_ADMIN, farmId],
    queryFn: () => getFieldShapesAdmin({ farmId }),
    ...DEFAULT_CACHE,
  });

  useEffect(() => {
    const newGeometry = fieldShapeResponse?.fieldShapes?.find(({ fieldId }) => fieldId === field.id);

    if (newGeometry) {
      setFieldShape(newGeometry);
      setLocation(newGeometry.center);
    }
  }, [fieldShapeResponse?.fieldShapes, field.id]);

  // Sync fieldSize state with field.size when field prop updates (e.g., after cache invalidation)
  useEffect(() => {
    setFieldSize(field.size);
  }, [field.size]);

  // Due to order the most current field verification is index 0
  const fieldVerificationIdCurrentYear = field?.verifications[0]?.id;

  // We need to load the field shape which was saved at the deadline, because it might have changed afterwards
  const { data: fieldVerificationShapeResponse } = useQuery({
    enabled: visible && !!fieldVerificationIdCurrentYear,
    queryKey: [GET_FIELD_VERIFICATION_SHAPE, fieldVerificationIdCurrentYear],
    queryFn: () => getFieldVerificationShape({ verificationId: fieldVerificationIdCurrentYear }),
  });

  const fieldVerificationShape = fieldVerificationShapeResponse?.fieldShape?.geometry?.length ? fieldVerificationShapeResponse.fieldShape : null;

  const { data: fieldSizeUpdates, isLoading: isLoadingFieldSizeUpdates } = useQuery({
    enabled: visible && !!field,
    queryKey: [GET_FIELD_SIZE_UPDATES, field.id],
    queryFn: () => getFieldSizeUpdates({ fieldId: field.id }),
  });

  const { data: fieldPractices, isLoading: isLoadingFieldPractices } = useGetFieldPractices({
    fieldId: field.id,
    inView: visible,
    isAdmin: true,
  });

  const { t } = useTranslation("practices", { useSuspense: false });

  const translateCropName = (translationSlug: string | null | undefined): string => {
    if (!translationSlug) return "-";
    return t(`crops.${translationSlug}`);
  };

  const cropTypeSortOrder: Record<string, number> = {
    [CROP_TYPE_FCPO.MAIN]: 0,
    [CROP_TYPE_FCPO.COVER]: 1,
    [CROP_TYPE_FCPO.UNDERSOWING]: 2,
  };
  const cropsPerYearRows = (fieldPractices?.crops ?? [])
    .slice()
    .sort((a, b) => {
      const yearDiff = (b.year ?? 0) - (a.year ?? 0);
      if (yearDiff !== 0) return yearDiff;
      return (cropTypeSortOrder[a.cropType] ?? 99) - (cropTypeSortOrder[b.cropType] ?? 99);
    })
    // note: dates are pre-formatted as localized strings so AdminTable treats them as string
    // columns and preserves insertion order (otherwise it auto-sorts date columns to the end)
    .map((crop) => ({
      year: crop.year ?? null,
      cropName: translateCropName(crop.translationSlug),
      cropType: crop.cropType ?? "-",
      seedingStart: crop.seedingStartDate ? formatDayDate(crop.seedingStartDate) : "-",
      harvestEnd: crop.harvestEndDate ? formatDayDate(crop.harvestEndDate) : "-",
      yield: crop.yieldPerHa != null ? `${crop.yieldPerHa} ${getUnitDisplayLabel(crop.yieldPerHaUnit)}` : "-",
      status: crop.verificationStatus ?? "-",
    }));

  const { mutate: updateFieldShape, isPending: isUpdatingShapeUpdates } = useUpdateFieldShapeAdmin({
    farmId,
    fieldId: field.id,
    fieldVerificationId: fieldVerificationIdCurrentYear,
  });

  const handleChangeShape = () => {
    if (!fieldShape) {
      return;
    }

    const center = getCenter(fieldShape.geometry);
    updateFieldShape(
      {
        fieldId: field.id,
        geometry: fieldShape,
        center,
      },
      {
        onSuccess: () => {
          setShowFieldEditDrawer(false);
          setShowModal(false);
          onDataChange();
        },
      }
    );
  };

  const invalidateFields = () => queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS, farmId] });

  const { mutate: mutateFieldRedzone } = useMutation({
    mutationKey: [CONFIRM_FIELD_REDZONE_ADMIN, field.id],
    mutationFn: confirmFieldRedzone,
    onSuccess: () => {
      setShowRedzoneModal(false);
      onDataChange();
      invalidateFields();
    },
  });

  const { mutate: mutateFieldVerification } = useMutation({
    mutationKey: [VERIFY_FIELD, field.id],
    mutationFn: verifyField,
    onSuccess: () => {
      onDataChange();
      invalidateFields();
      message({
        content: "Updating verification state successful",
        icon: "gremlin",
      });
    },
  });

  const confirmRedzone = () => {
    if (!field?.redzone) {
      return;
    }

    mutateFieldRedzone({
      fieldId: field.id,
      year: field.redzone.year,
    });
  };

  // This variable of the FieldShapeVerification component will be reused for both drawers
  const fieldShapeVerification = field && !isLoadingFieldShapes && !isUpdatingShapeUpdates && (
    <FieldShapeVerification
      field={field}
      farmId={farmId}
      showDrawing={showFieldShapeDrawer}
      geometries={fieldShapeResponse?.fieldShapes}
      overrideUserReportedGeometry={fieldVerificationShape}
      tiny={!showFieldShapeDrawer}
      markers={[
        {
          location: field?.location,
          name: field?.name,
        },
      ]}
      belowNoDigifarm={
        !showFieldShapeDrawer ? (
          <Button type="primary" onClick={() => setShowFieldShapeDrawer(true)} className="draw-shape-button">
            Draw a shape
          </Button>
        ) : null
      }
      editIcon={
        !showFieldShapeDrawer ? (
          <div className="edit-icon digifarm-edit-icon">
            <Icon iconName="klim/edit-element" className="icon inline" onClick={() => setShowFieldShapeDrawer(true)} />
          </div>
        ) : null
      }
    />
  );

  return (
    <>
      <Drawer visible={visible} onClose={onClose} title="Field verification" className="field-drawer">
        {field && (
          <>
            <Row>
              <Col>
                <AdminDescriptions
                  columns={2}
                  items={[
                    {
                      label: null,
                      labelStyle: { display: "none" },
                      span: 2,
                      children: (
                        <>
                          {field.verifications.some(({ verified }) => verified === null) && !field.verifications.some(({ verified }) => verified === false) && (
                            <Tag text="pending verifications" colour="yellow" className="margin-bottom-50" />
                          )}
                          <Typography variant="h2">{field.name}</Typography>
                        </>
                      ),
                    },
                    ...field.verifications.map(({ id, year, verified }) => ({
                      label: `Verification ${year}`,
                      span: 2,
                      children: (
                        <FieldVerificationForm
                          verified={verified}
                          name={`verification-status-form-${id}`}
                          onChange={(value: VerificationStatus) => mutateFieldVerification({ verificationId: id, verified: value })}
                          disabled={payoutApproved}
                        />
                      ),
                    })),
                    {
                      label: "Comments",
                      span: 1,
                      children: (
                        <>
                          <Icon iconName={"tabler/message-2"} className="icon inline margin-right-25" />
                          {commentCount}
                        </>
                      ),
                    },
                    {
                      label: "FLIK number",
                      span: 1,
                      children: field.flik || "unknown",
                    },
                    {
                      label: "Field number",
                      span: 1,
                      children: field.number || "unknown",
                    },
                    {
                      label: "Shape source",
                      span: 1,
                      children: (
                        <SkeletonWrapper isLoading={isLoadingFieldShapes} type="button" buttonShape="round" buttonSize="small">
                          {fieldShapeResponse?.fieldShapes.some(({ fieldId, manual }) => fieldId === field.id && manual) ? "created manually" : "uploaded"}
                        </SkeletonWrapper>
                      ),
                    },
                    {
                      label: "NDVI scores",
                      span: 2,
                      children: (
                        <Link to={`/verifications/ndvi-scores?fieldId=${field.id}`} target="_blank">
                          Link to NDVI graph and images
                        </Link>
                      ),
                    },
                    {
                      label: "Copernicus Browser",
                      span: 2,
                      children: field.location ? (
                        <Link
                          to={`https://browser.dataspace.copernicus.eu/?lat=${field.location.lat}&lng=${field.location.lng}&zoom=17&layerId=TRUE_COLOR`}
                          target="_blank"
                        >
                          View field in Copernicus Data Space Browser
                        </Link>
                      ) : (
                        "Location unavailable"
                      ),
                    },
                    {
                      label: "Editing request",
                      span: 2,
                      children: (
                        <Button type={buttonState.type} className={buttonState.className} onClick={() => setEditingRequestField(field)}>
                          {buttonState.text}
                        </Button>
                      ),
                    },
                    {
                      label: "Crops verification",
                      span: 2,
                      children: (
                        <Button type="primary" onClick={() => window.open(`/verifications/farms/${farmId}/crops`, "_blank")}>
                          Go to crops verification
                        </Button>
                      ),
                    },
                    {
                      label: "Field ID",
                      span: 2,
                      children: field.id || "unknown",
                    },
                  ]}
                />
              </Col>
            </Row>

            <Row className="padding-bottom-100">
              <Col>{fieldShapeVerification}</Col>
            </Row>

            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="paragraph-m" bold onClick={() => setShowFieldEditDrawer(true)} color="green-400" className="link">
                  Change field shape
                </Typography>
              </Col>
            </Row>

            {/* disabled for now */}
            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="h2" className="margin-bottom-100">
                  Change field verification size
                </Typography>
                <Typography variant="paragraph-m">This won't show up in field size changes below</Typography>

                {!loadingFields && !!fieldVerificationIdCurrentYear && (
                  <FieldVerificationSizeForm
                    verificationId={fieldVerificationIdCurrentYear}
                    size={field.size}
                    farmId={farmId}
                    onSuccess={onDataChange}
                    key={selectedFieldVersion}
                  />
                )}

                {!fieldVerificationIdCurrentYear && (
                  <div>
                    <Typography variant="paragraph-m">Field not in verification yet</Typography>
                  </div>
                )}
              </Col>
            </Row>

            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="h2" className="margin-bottom-100">
                  Field size variance calculator
                </Typography>
                <Typography variant="paragraph-m" className="margin-bottom-100">
                  Compare agrar-antrag size with platform size ({field.size?.toFixed(2) ?? "-"} ha)
                </Typography>

                {!loadingFields && field.verifications[0] && !!fieldVerificationIdCurrentYear && (
                  <FieldSizeVarianceCalculatorForm
                    verificationId={fieldVerificationIdCurrentYear}
                    platformSize={field.size}
                    officialSize={field.verifications[0]?.officialSize ?? null}
                    farmId={farmId}
                    onSuccess={onDataChange}
                    key={`variance-${selectedFieldVersion}`}
                  />
                )}

                {!fieldVerificationIdCurrentYear && (
                  <div>
                    <Typography variant="paragraph-m">Field not in verification yet</Typography>
                  </div>
                )}
              </Col>
            </Row>

            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="h2" className="margin-bottom-100">
                  Tracked sizes
                </Typography>
                {field.verifications.some((v) => v.officialSize != null) ? (
                  /* @ts-expect-error not yet in TS */
                  <AdminTable
                    dataSource={field.verifications
                      .filter((v): v is typeof v & { officialSize: number } => v.officialSize != null)
                      .map((v) => ({
                        year: v.year,
                        platformSize: `${v.size.toFixed(2)} ha`,
                        officialSize: `${v.officialSize.toFixed(2)} ha`,
                        variance: `${(((v.officialSize - v.size) / v.size) * 100).toFixed(2)}%`,
                      }))}
                    config={{
                      exclude: ["id"],
                    }}
                  />
                ) : (
                  <Typography variant="paragraph-m">No official sizes have been recorded yet</Typography>
                )}
              </Col>
            </Row>

            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="h2" className="margin-bottom-100">
                  Field size changes
                </Typography>
                <SkeletonWrapper isLoading={isLoadingFieldSizeUpdates}>
                  {!fieldSizeUpdates?.length ? (
                    <Typography variant="paragraph-m">This field has not changed size since being added to the platform</Typography>
                  ) : (
                    <>
                      <Typography variant="paragraph-m">Field changes are ordered by date, most recent first</Typography>
                      {/* @ts-expect-error not yet in TS */}
                      <AdminTable
                        dataSource={fieldSizeUpdates}
                        config={{
                          exclude: ["id", "farmId", "fieldId", "createdAt"],
                        }}
                      />
                    </>
                  )}
                </SkeletonWrapper>
              </Col>
            </Row>

            <Row className="padding-bottom-400">
              <Col>
                <Typography variant="h2" className="margin-bottom-100">
                  Crops per year
                </Typography>
                <SkeletonWrapper isLoading={isLoadingFieldPractices}>
                  {cropsPerYearRows.length === 0 ? (
                    <Typography variant="paragraph-m">No crops have been added to this field</Typography>
                  ) : (
                    <>
                      <Typography variant="paragraph-m">Crops are ordered by year (most recent first) and then by crop type</Typography>
                      {/* @ts-expect-error not yet in TS */}
                      <AdminTable
                        dataSource={cropsPerYearRows}
                        config={{
                          exclude: ["id"],
                        }}
                      />
                    </>
                  )}
                </SkeletonWrapper>
              </Col>
            </Row>

            {field.redzone ? (
              <Row className="padding-bottom-400">
                <Col>
                  <SkeletonWrapper isLoading={loadingFields}>
                    <Typography variant="h2" className="margin-bottom-100">
                      Redzone information
                    </Typography>
                    <Card className="map-card tiny">
                      <ErrorBoundary>
                        {/* @ts-expect-error not yet in TS */}
                        <MapInteractive
                          field={field}
                          geometry={{ geometry: field.redzone.geometry }}
                          location={field.location}
                          markers={[
                            {
                              location: field?.location,
                              name: field?.name,
                            },
                          ]}
                          scaleControl={true}
                          zoomControl={true}
                          defaultZoom={8}
                        />
                      </ErrorBoundary>
                    </Card>

                    {!field.redzone.confirmed ? (
                      <>
                        <Typography variant="paragraph-m">
                          The user has indicated that this field is not in a redzone. Press the button below to confirm the field is in a redzone, and to
                          recalculate the footprints for the modules added for {field.redzone.year}.
                        </Typography>
                        <Button type="primary" onClick={() => setShowRedzoneModal(true)}>
                          Confirm field is in redzone
                        </Button>
                      </>
                    ) : (
                      <Typography variant="paragraph-m">This field is confirmed to be in the redzone displayed above.</Typography>
                    )}
                  </SkeletonWrapper>
                </Col>
              </Row>
            ) : null}

            {!!fieldVerificationIdCurrentYear && (
              <Row className="margin-top-200">
                <Col>
                  <VerificationComments
                    verificationFieldId={fieldVerificationIdCurrentYear}
                    farmId={farmId}
                    type={"field"}
                    setCommentCount={setCommentCount}
                    onCommentCreate={onDataChange}
                  />
                </Col>
              </Row>
            )}
          </>
        )}

        <Drawer visible={showFieldShapeDrawer} onClose={() => setShowFieldShapeDrawer(false)} title="Verification tool" wide>
          {fieldShapeVerification}
        </Drawer>
        {location && fieldShape && (
          <FieldLocation
            admin
            farm={farm}
            field={field}
            fieldSize={fieldSize}
            parentFieldShape={fieldShape}
            handleChangeShape={handleChangeShape}
            loadingShapeUpdates={isUpdatingShapeUpdates || isLoadingFieldShapes}
            parentLocation={location}
            onClose={() => setShowModal(true)}
            setFieldSize={setFieldSize}
            setParentFieldShape={setFieldShape}
            setParentLocation={(v: Coordinates | null) => setLocation(v)}
            setShowAdminModalConfirm={setShowModal}
            setShowFieldEditDrawer={setShowFieldEditDrawer}
            showAdminModalConfirm={showModal}
            showLocationMessage={() => {}}
            showMapDrawer={showFieldEditDrawer}
          />
        )}

        {field.redzone ? (
          <Modal
            cancelText="close"
            content={
              <>
                <Typography variant="paragraph-m" bold>
                  Are you sure?
                </Typography>
                <Typography variant="paragraph-m">
                  Pushing this button will trigger a recalculation of all modules for {field.redzone.year} on this field. As this field will now be in a
                  redzone, all payments from these modules will be affected.
                </Typography>
              </>
            }
            onCancel={() => setShowRedzoneModal(false)}
            onOk={() => confirmRedzone()}
            okText="confirm field is in redzone"
            showModal={showRedzoneModal}
            title="Confirm field is in redzone"
          />
        ) : null}

        <EditingRequestModal
          fieldId={editingRequestField?.id ?? ""}
          farmId={farmId}
          editingRequests={fieldEditingRequests}
          showModal={!!editingRequestField}
          onClose={() => setEditingRequestField(null)}
          verificationYear={verificationYear}
        />
      </Drawer>
    </>
  );
};
